import React, { useEffect, useState } from 'react';

// Subtle "Add Lantern to your home screen" banner. Driven by the Chromium
// `beforeinstallprompt` event — Safari/iOS doesn't fire this, so iOS users
// see no banner and instead use Share → Add to Home Screen, which the meta
// tags in index.html configure for.
//
// Suppressed when:
//   - we're not on a small screen (mobile-only per spec)
//   - the app is already running standalone (installed)
//   - the user previously dismissed the banner (localStorage flag)

const DISMISSED_KEY = 'lantern.installPrompt.dismissed';
const MOBILE_QUERY  = '(max-width: 640px)';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true; // iOS
}

function isMobile() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.(MOBILE_QUERY).matches ?? false;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isMobile()) return;
    try {
      if (window.localStorage.getItem(DISMISSED_KEY) === '1') return;
    } catch { /* localStorage unavailable — fall through and show */ }

    function handler(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    }
    window.addEventListener('beforeinstallprompt', handler);

    // If the install completes in another tab, hide the banner here too.
    function installed() { setVisible(false); }
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  function dismissPermanently() {
    try { window.localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* ignore */ }
    // Per spec: dismiss permanently when tapped, regardless of accept/decline.
    dismissPermanently();
    setDeferredPrompt(null);
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Install Lantern"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '0 auto 16px',
        maxWidth: 460,
        padding: '10px 12px',
        borderRadius: 14,
        border: '1.5px solid #F0E7D5',
        background: '#FFFFFF',
        boxShadow: '0 2px 0 #F0E7D5',
        fontFamily: "'Source Sans 3', sans-serif",
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1 }}>📲</span>
      <button
        type="button"
        onClick={handleInstall}
        style={{
          flex: 1,
          textAlign: 'left',
          padding: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: 14,
          fontWeight: 600,
          color: '#1A1F36',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        Add Lantern to your home screen
      </button>
      <button
        type="button"
        onClick={dismissPermanently}
        aria-label="Dismiss"
        style={{
          width: 32,
          height: 32,
          padding: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#5A6478',
          fontSize: 20,
          lineHeight: 1,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        ×
      </button>
    </div>
  );
}
